import React, { useCallback, useRef, useState } from 'react';
import { message, Skeleton } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import { appStyles } from '../../../styles';
import { getQuestionsRepository } from '../../../repository/mytm/home/cartnerConvosRepository';
import { PER_PAGE_OTHER } from '../../../constants/common';
import { MESSAGE_ERROR } from '../../../constants/message';
import { CartnerConvosTable } from '../../organisms/cartner-convos/CartnerConvosTable';
import { CartnerConvosHeader } from '../../organisms/cartner-convos/CartnerConvosHeader';
import { FormCartnerConvosModal } from '../../organisms/cartner-convos/FormCartnerConvosModal';
import { FormScheduleTileModal } from '../../organisms/cartner-convos/FormScheduleTileModal';
import { FormSummaryModal } from '../../organisms/cartner-convos/FormSummaryModal';

export const CartnerConvos = () => {
  const [pageSize, setPageSize] = useState(PER_PAGE_OTHER);
  const [pageActive, setPageActive] = useState(1);
  const [loading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenScheduleModal, setIsOpenScheduleModal] = useState(false);
  const [isOpenSummaryModal, setIsOpenSummaryModal] = useState(false);
  const [scheduleRecord, setDataScheduleRecord] = useState<any>();
  const [record, setDataRecord] = useState<any>();
  const [summaryRecord, setSummaryRecord] = useState<any>();
  const errorCount = useRef(0);

  const { data, isValidating, error, mutate } = useSWR(
    [pageActive, 1, pageSize],
    (pageNumber: number) =>
      getQuestionsRepository({
        page: pageNumber,
        limit: pageSize,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  const onCloseScheduleModal = useCallback(() => {
    setIsOpenScheduleModal(false);
  }, []);

  const onCloseSummaryModal = useCallback(() => {
    setIsOpenSummaryModal(false);
    setSummaryRecord(null);
  }, []);

  const handleChangePageActive = useCallback(value => {
    setPageActive(value);
    window.scrollTo(0, 0);
  }, []);

  const handleChangeShowSize = useCallback((current: number, size: number) => {
    setPageActive(current);
    setPageSize(size);
    window.scrollTo(0, 0);
  }, []);

  if (error) {
    errorCount.current += 1;
    if (errorCount.current === 1) {
      message.error(MESSAGE_ERROR.M02);
      const timeout = setTimeout(() => {
        errorCount.current = 0;
        clearTimeout(timeout);
      }, 5000);
    }
  }

  const recordQuestions = data?.questions?.map((item: any, index: number) => ({ ...item, ...{ index } }));

  return (
    <Container>
      <CartnerConvosHeader setIsOpenModal={setIsOpenModal} />
      {!data && (
        <Container>
          <Skeleton active />
        </Container>
      )}
      {data && (
        <CartnerConvosTable
          handleChangePageActive={handleChangePageActive}
          onRefresh={mutate}
          records={recordQuestions || []}
          loading={loading || isValidating}
          setIsOpenModal={setIsOpenModal}
          setDataRecord={setDataRecord}
          pagination={{
            onChange: handleChangePageActive,
            pageSize,
            current: pageActive,
            total: data?.total,
            onShowSizeChange: handleChangeShowSize,
            showSizeChanger: true,
          }}
          setIsOpenScheduleModal={setIsOpenScheduleModal}
          setDataScheduleRecord={setDataScheduleRecord}
          setIsOpenSummaryModal={setIsOpenSummaryModal}
          setSummaryRecord={setSummaryRecord}
        />
      )}

      {isOpenModal && (
        <FormCartnerConvosModal
          setDataRecord={setDataRecord}
          record={record}
          onRefresh={mutate}
          isOpenModal={isOpenModal}
          onClose={onCloseModal}
        />
      )}

      {isOpenScheduleModal && (
        <FormScheduleTileModal
          setDataRecord={setDataScheduleRecord}
          record={scheduleRecord}
          isOpenModal={isOpenScheduleModal}
          onClose={onCloseScheduleModal}
          onRefresh={mutate}
        />
      )}

      {isOpenSummaryModal && (
        <FormSummaryModal record={summaryRecord} isOpenModal={isOpenSummaryModal} onClose={onCloseSummaryModal} set />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .ant-table-thead {
    border-bottom: 2px solid #eee;
  }
  .ant-table-thead > tr > th {
    ${appStyles.background.tableBgGray}
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: 600;
    &:before {
      display: none;
    }
  }

  & .ant-page-header {
    padding-left: 0px;
  }
`;
